import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ScholarshipsService } from '../../../services/scholarships/scholarships.service';
import { GetScholarshipRequest, ScholarshipType } from '../../../models/scholarships/getScholarshipRequest';
import { ScholarshipResponse } from '../../../models/scholarships/scholarshipResponse';
import { MessageService } from 'primeng/api';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import * as L from 'leaflet';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { ButtonModule } from 'primeng/button';
import { RouterModule } from '@angular/router';

interface CountryStat {
  name: string;
  code: string;
  total: number;
  fullFunding: number;
  totalValueFormatted: string;
  intensity: number;
}

@Component({
  selector: 'app-scholarships-map',
  standalone: true,
  imports: [CommonModule, ProgressSpinnerModule, LeafletModule, ButtonModule, RouterModule],
  templateUrl: './scholarships-map.component.html',
  styleUrls: ['./scholarships-map.component.scss']
})
export class ScholarshipsMapComponent implements OnInit, OnDestroy {

  isLoading = false;
  allScholarshipsList: ScholarshipResponse[] = [];
  countryStats = new Map<string, CountryStat>();
  
  viewMode: string = 'map';
  myScholarshipsOnly: boolean = false;
  
  private map!: L.Map;
  private geoJsonLayer: L.GeoJSON | null = null;
  private geoJsonData: any = null;

  // Leaflet Map Options
  mapOptions: L.MapOptions = {
    layers: [
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        maxZoom: 18,
        minZoom: 2,
        noWrap: true,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
      })
    ],
    zoom: 2,
    center: L.latLng(20, 0),
    zoomControl: false, // We'll use custom or just keep it simple
    dragging: true,
    touchZoom: true,
    scrollWheelZoom: true,
    doubleClickZoom: true,
    boxZoom: false,
    maxBounds: L.latLngBounds(L.latLng(-90, -180), L.latLng(90, 180)),
    maxBoundsViscosity: 1.0
  };

  layers: L.Layer[] = [];

  private countryAliases: { [key: string]: string } = {
    'usa': 'united states of america',
    'us': 'united states of america',
    'united states': 'united states of america',
    'uk': 'united kingdom',
    'uae': 'united arab emirates',
    'england': 'united kingdom'
  };

  constructor(
    private router: Router,
    private http: HttpClient,
    private scholarshipsService: ScholarshipsService,
    private messageService: MessageService
  ) { }

  ngOnInit(): void {
    this.getScholarships();
  }

  ngOnDestroy(): void {
    if (this.map) {
      try {
        this.map.off();
        this.map.remove();
      } catch (e) {
        console.warn('Leaflet cleanup error:', e);
      }
    }
  }

  getScholarships() {
    this.isLoading = true;
    const req: GetScholarshipRequest = {
      scholarshipName: '',
      scholarshipType: ScholarshipType.Scholarship,
      stream: null,
      level: null,
    };

    this.scholarshipsService.getScholarships(req).subscribe({
      next: (response) => {
        this.allScholarshipsList = response ? response.result : [];
        this.processMapData(); // Process what we have
        this.loadGeoJson();    // Then load shapes
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: `Error Fetching Scholarships: ${err}` });
      }
    });
  }

  loadGeoJson() {
    const worldUrl = 'https://raw.githubusercontent.com/johan/world.geo.json/master/countries.geo.json';
    const indiaUrl = 'https://raw.githubusercontent.com/datameet/maps/master/Country/india-land-simplified.geojson';
    
    Promise.all([
      fetch(worldUrl).then(res => res.json()),
      fetch(indiaUrl).then(res => res.json())
    ])
    .then(([worldData, indiaData]) => {
      // Remove original India (which may show incorrect boundaries)
      if (worldData && worldData.features) {
        worldData.features = worldData.features.filter((f: any) => f.id !== 'IND' && f.properties?.name !== 'India');
      }
      
      // Setup correct properties for the new India boundary
      if (indiaData && indiaData.features) {
        indiaData.features.forEach((f: any) => {
          if (!f.properties) f.properties = {};
          f.properties.name = 'India';
          f.properties.ADMIN = 'India';
          f.id = 'IND';
        });
        // Merge into world data
        worldData.features = [...worldData.features, ...indiaData.features];
      }
      
      this.geoJsonData = worldData;
      this.renderGeoJson();
    })
    .catch(err => {
      console.error('Error loading GeoJSON:', err);
    });
  }

  processMapData() {
    if (!this.allScholarshipsList || this.allScholarshipsList.length === 0) return;
    
    this.countryStats.clear();
    
    // Count scholarships per country
    this.allScholarshipsList.forEach(s => {
      const countries = Array.isArray(s.country) ? s.country : [s.country];
      countries.forEach(cName => {
        if (!cName) return;
        let normalized = cName.toLowerCase().trim();
        normalized = this.countryAliases[normalized] || normalized;
        
        let stat = this.countryStats.get(normalized);
        if (!stat) {
          stat = {
            name: cName,
            code: '',
            total: 0,
            fullFunding: 0,
            totalValueFormatted: '$0',
            intensity: 0
          };
          this.countryStats.set(normalized, stat);
        }
        
        stat.total++;
        if (s.fullFunding) stat.fullFunding++;
      });
    });

    // Calculate intensities
    const statsArray = Array.from(this.countryStats.values());
    const maxTotal = statsArray.length > 0 ? Math.max(...statsArray.map(s => s.total)) : 1;
    
    this.countryStats.forEach(stat => {
      stat.intensity = stat.total / maxTotal;
      stat.totalValueFormatted = `$${(stat.total * 0.05).toFixed(1)}M`;
    });

    this.renderGeoJson();
  }

  renderGeoJson() {
    if (!this.geoJsonData) return;

    this.geoJsonLayer = L.geoJSON(this.geoJsonData, {
      style: (feature) => this.getCountryStyle(feature),
      onEachFeature: (feature, layer) => this.onEachFeature(feature, layer)
    });

    // Use the ngx-leaflet bound layers array for automatic map management
    this.layers = [this.geoJsonLayer];
  }

  getCountryStyle(feature: any): L.PathOptions {
    const props = feature.properties;
    let countryName = props.ADMIN || props.name || props.NAME || '';
    let normalized = countryName.toLowerCase().trim();
    normalized = this.countryAliases[normalized] || normalized;
    
    const stat = this.countryStats.get(normalized);
    const intensity = stat ? stat.intensity : 0;

    return {
      fillColor: this.getColor(intensity),
      weight: 1,
      opacity: 1,
      color: '#cbd5e1', // Slate-300 for borders
      fillOpacity: intensity > 0 ? 0.9 : 0.4
    };
  }

  getColor(intensity: number): string {
    if (intensity === 0) return '#f8fafc';
    if (intensity < 0.2) return '#e0e7ff';
    if (intensity < 0.4) return '#c7d2fe';
    if (intensity < 0.6) return '#a5b4fc';
    if (intensity < 0.8) return '#818cf8';
    return '#4f46e5';
  }

  onEachFeature(feature: any, layer: L.Layer) {
    const props = feature.properties;
    let countryName = props.ADMIN || props.name || props.NAME || '';
    let normalized = countryName.toLowerCase().trim();
    normalized = this.countryAliases[normalized] || normalized;
    
    const stat = this.countryStats.get(normalized);

    if (stat && stat.total > 0) {
      const popupContent = `
        <div class="flex flex-column" style="width: 280px; font-family: 'Karla', sans-serif;">
          <div style="padding: 16px; background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%); color: white;">
            <h3 style="margin: 0; font-size: 16px; font-weight: 700;">${stat.name}</h3>
          </div>
          
          <div style="padding: 16px; display: flex; flex-direction: column; gap: 12px; background: white;">
            <div style="display: flex; align-items: center; justify-content: space-between;">
              <div style="display: flex; align-items: center; gap: 10px;">
                <div style="width: 28px; height: 28px; border-radius: 6px; background: #f1f5f9; display: flex; align-items: center; justify-content: center;">
                  <i class="pi pi-list" style="color: #4f46e5; font-size: 14px;"></i>
                </div>
                <span style="font-size: 14px; color: #475569;">Active Scholarships</span>
              </div>
              <span style="font-weight: 700; font-size: 14px; color: #1e293b;">${stat.total}</span>
            </div>

            <div style="display: flex; align-items: center; justify-content: space-between;">
              <div style="display: flex; align-items: center; gap: 10px;">
                <div style="width: 28px; height: 28px; border-radius: 6px; background: #f1f5f9; display: flex; align-items: center; justify-content: center;">
                  <i class="pi pi-shield" style="color: #4f46e5; font-size: 14px;"></i>
                </div>
                <span style="font-size: 14px; color: #475569;">Full Funding</span>
              </div>
              <span style="font-weight: 700; font-size: 14px; color: #1e293b;">${stat.fullFunding}</span>
            </div>
            
            <div style="padding-top: 8px; margin-top: 4px; border-top: 1px solid #f1f5f9;">
              <button id="explore-${stat.name.replace(/\s+/g, '-')}" class="explore-btn"
                      style="width: 100%; padding: 12px; border-radius: 8px; border: none; background-color: #4f46e5; color: white; font-weight: 600; font-size: 14px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;">
                Explore Opportunities <i class="pi pi-arrow-right" style="font-size: 12px;"></i>
              </button>
            </div>
          </div>
        </div>
      `;

      layer.bindPopup(popupContent, {
        maxWidth: 280,
        className: 'custom-scholarship-popup'
      });

      layer.on('popupopen', () => {
        const btn = document.getElementById(`explore-${stat.name.replace(/\s+/g, '-')}`);
        if (btn) {
          btn.onclick = () => this.exploreRegion(stat.name);
        }
      });

      // Highlight on hover
      layer.on('mouseover', () => {
        if (layer instanceof L.Path) {
          layer.setStyle({
            fillOpacity: 1,
            weight: 2,
            color: '#4f46e5'
          });
        }
      });
      layer.on('mouseout', () => {
        if (this.geoJsonLayer && layer instanceof L.Path) {
          this.geoJsonLayer.resetStyle(layer);
        }
      });
    }
  }

  onMapReady(map: L.Map) {
    this.map = map;
    setTimeout(() => {
      map.invalidateSize();
      if (this.geoJsonData) {
        this.processMapData();
      }
    }, 200);
  }

  goToCards() {
    this.router.navigate(['/portal/scholarships']);
  }
  
  goToMap() {
    this.myScholarshipsOnly = false;
    this.viewMode = 'map';
  }

  goToCalendar() {
    this.router.navigate(['/portal/deadline-calendar']);
  }

  goToMyScholarships() {
    this.router.navigate(['/portal/scholarships'], { queryParams: { myScholarships: true } });
  }

  exploreRegion(regionName: string) {
    this.router.navigate(['/portal/scholarships'], { queryParams: { country: regionName } });
  }
}
